import type { CameraState, ScalarGrid, Size } from '../types';
import { sampleLattice } from '../data/sampleLattice';
import { isSameRange } from '../data/ranges';
import type { CollapsedSample } from '../data/collapseCoincident';
import { createProjector } from '../geometry/projection';
import { isSameCamera } from '../interaction/orbitCamera';
import {
  drawTooltip,
  toComponentHeading,
  toComponentNameLines,
} from './renderTooltip';

/** Radius of a resting marker, in pixels. Small enough to read as annotation, not as data. */
const MARKER_RADIUS = 2.5;

const HOVERED_RADIUS = 5;

/** Ring marking a sample that carries a component the host named as context. */
const CONTEXT_RING_WIDTH = 1.25;
const CONTEXT_RING_GAP = 2.5;

/** How close the cursor must come, in pixels. Generous, because the markers are deliberately small. */
const PICK_RADIUS = 14;

/**
 * The gridded surface's height at a field position.
 *
 * Bilinear, and propagating null: a sample outside the interpolated support has no surface beneath
 * it at all, which is a different statement from one sitting at height zero.
 */
const surfaceValueAt = (
  grid: ScalarGrid,
  fieldX: number,
  fieldY: number
): number | null => {
  /* The sampling domain, not the axes: this indexes the lattice, and the lattice was rasterised
     over the data's own extent. Against a host-widened axis every lookup would land short of the
     cell it means, by the fraction that axis was widened by. */
  const [xMin, xMax] = grid.dataXRange;
  const [yMin, yMax] = grid.dataYRange;
  const gx = ((fieldX - xMin) / (xMax - xMin || 1)) * (grid.width - 1);
  const gy = ((fieldY - yMin) / (yMax - yMin || 1)) * (grid.height - 1);
  return sampleLattice(grid, gx, gy, 'propagate');
};

/**
 * A sample in the unit cube the projector takes: x and y in [-1, 1], z in [0, 1].
 *
 * Samples carry the field's own coordinates, so they are normalized the same way the mesh was —
 * anything else places them off the surface they are meant to sit on. Exported because the
 * crosshair needs the same cube coordinates rather than the screen ones, and two copies of this
 * arithmetic would be two chances to disagree about where a point is.
 */
export const toUnitPoint = (
  grid: ScalarGrid,
  sample: { x: number; y: number; z: number }
): [x: number, y: number, z: number] => {
  const [xMin, xMax] = grid.xRange;
  const [yMin, yMax] = grid.yRange;
  const [zMin, zMax] = grid.zRange;

  return [
    ((sample.x - xMin) / (xMax - xMin || 1)) * 2 - 1,
    ((sample.y - yMin) / (yMax - yMin || 1)) * 2 - 1,
    (sample.z - zMin) / (zMax - zMin || 1),
  ];
};

/**
 * Each sample's position in the unit cube and the height of the surface beneath it, indexed like
 * `samples`. Depends on the data and the grid, not on the camera.
 */
export type SampleAnchors = {
  samples: CollapsedSample[];
  grid: ScalarGrid;
  unitX: Float64Array;
  unitY: Float64Array;
  unitZ: Float64Array;
  /** The surface's normalized height under the sample, meaningful only where `hasSurface` is 1. */
  surfaceUnitZ: Float64Array;
  hasSurface: Uint8Array;
  hasContext: Uint8Array;
  indexBySample: Map<CollapsedSample, number>;
};

export const buildSampleAnchors = (
  samples: CollapsedSample[],
  grid: ScalarGrid
): SampleAnchors => {
  const [zMin, zMax] = grid.zRange;
  const zSpan = zMax - zMin || 1;
  const unitX = new Float64Array(samples.length);
  const unitY = new Float64Array(samples.length);
  const unitZ = new Float64Array(samples.length);
  const surfaceUnitZ = new Float64Array(samples.length);
  const hasSurface = new Uint8Array(samples.length);
  const hasContext = new Uint8Array(samples.length);
  const indexBySample = new Map<CollapsedSample, number>();

  samples.forEach((sample, index) => {
    const [x, y, z] = toUnitPoint(grid, sample);
    const surface = surfaceValueAt(grid, sample.x, sample.y);
    unitX[index] = x;
    unitY[index] = y;
    unitZ[index] = z;
    if (surface !== null) {
      surfaceUnitZ[index] = (surface - zMin) / zSpan;
      hasSurface[index] = 1;
    }
    if (sample.points.some(({ isContext }) => isContext)) {
      hasContext[index] = 1;
    }
    indexBySample.set(sample, index);
  });

  return {
    samples,
    grid,
    unitX,
    unitY,
    unitZ,
    surfaceUnitZ,
    hasSurface,
    hasContext,
    indexBySample,
  };
};

const isSameGrid = (left: ScalarGrid, right: ScalarGrid): boolean =>
  left.values === right.values &&
  left.width === right.width &&
  left.height === right.height &&
  isSameRange(left.xRange, right.xRange) &&
  isSameRange(left.yRange, right.yRange) &&
  isSameRange(left.zRange, right.zRange) &&
  isSameRange(left.dataXRange, right.dataXRange) &&
  isSameRange(left.dataYRange, right.dataYRange);

const isSampleAnchorsFor = (
  anchors: SampleAnchors,
  samples: CollapsedSample[],
  grid: ScalarGrid
): boolean => anchors.samples === samples && isSameGrid(anchors.grid, grid);

/**
 * Where each sample's marker, plumb line and tether land for one camera and viewport, indexed like
 * `anchors.samples`. In CSS pixels, so the device pixel ratio does not enter it.
 */
export type SampleScreenGeometry = {
  anchors: SampleAnchors;
  camera: CameraState;
  viewport: Size;
  screenX: Float64Array;
  screenY: Float64Array;
  baseY: Float64Array;
  /** Where the fitted surface sits, meaningful only where `anchors.hasSurface` is 1. */
  surfaceY: Float64Array;
};

export const buildSampleScreenGeometry = (
  anchors: SampleAnchors,
  camera: CameraState,
  viewport: Size
): SampleScreenGeometry => {
  const project = createProjector(camera, viewport);
  const { unitX, unitY, unitZ, surfaceUnitZ, hasSurface } = anchors;
  const count = anchors.samples.length;
  const screenX = new Float64Array(count);
  const screenY = new Float64Array(count);
  const baseY = new Float64Array(count);
  const surfaceY = new Float64Array(count);

  for (let index = 0; index < count; index++) {
    const top = project(unitX[index], unitY[index], unitZ[index]);
    screenX[index] = top.screenX;
    screenY[index] = top.screenY;
    baseY[index] = project(unitX[index], unitY[index], 0).screenY;
    if (hasSurface[index] === 1) {
      surfaceY[index] = project(
        unitX[index],
        unitY[index],
        surfaceUnitZ[index]
      ).screenY;
    }
  }

  return { anchors, camera, viewport, screenX, screenY, baseY, surfaceY };
};

const isSampleScreenGeometryFor = (
  geometry: SampleScreenGeometry,
  camera: CameraState,
  viewport: Size
): boolean =>
  isSameCamera(geometry.camera, camera) &&
  geometry.viewport.width === viewport.width &&
  geometry.viewport.height === viewport.height;

export type SampleScreenInputs = {
  samples: CollapsedSample[];
  grid: ScalarGrid;
  camera: CameraState;
  viewport: Size;
};

/** The geometry for `inputs`, reusing `geometry`, or only its anchors, wherever still current. */
export const refreshSampleScreenGeometry = (
  geometry: SampleScreenGeometry | null,
  { samples, grid, camera, viewport }: SampleScreenInputs
): SampleScreenGeometry => {
  if (!geometry || !isSampleAnchorsFor(geometry.anchors, samples, grid)) {
    return buildSampleScreenGeometry(
      buildSampleAnchors(samples, grid),
      camera,
      viewport
    );
  }
  if (isSampleScreenGeometryFor(geometry, camera, viewport)) return geometry;
  return buildSampleScreenGeometry(geometry.anchors, camera, viewport);
};

export type SampleScreenPosition = {
  screenX: number;
  screenY: number;
  baseY: number;
  /** Where the fitted surface sits at this position, or null where it has no data. */
  surfaceY: number | null;
};

const toScreenPosition = (
  { anchors, screenX, screenY, baseY, surfaceY }: SampleScreenGeometry,
  index: number
): SampleScreenPosition => ({
  screenX: screenX[index],
  screenY: screenY[index],
  baseY: baseY[index],
  surfaceY: anchors.hasSurface[index] === 1 ? surfaceY[index] : null,
});

/** Where `sample` lands, or null when `geometry` was not built for it. */
export const sampleScreenPosition = (
  geometry: SampleScreenGeometry,
  sample: CollapsedSample
): SampleScreenPosition | null => {
  const index = geometry.anchors.indexBySample.get(sample);
  if (index === undefined) return null;
  return toScreenPosition(geometry, index);
};

/**
 * The sample nearest the cursor, if any is close enough.
 *
 * Nearest in screen space rather than the frontmost within a radius, because these are annotations
 * on a surface rather than solid objects: the one whose marker the cursor is closest to is the one
 * a viewer means, even if another sits slightly in front of it. On a tie the later sample wins.
 */
export const findSampleAt = (
  { anchors: { samples }, screenX, screenY }: SampleScreenGeometry,
  cursor: { x: number; y: number }
): CollapsedSample | null => {
  let best = -1;
  let bestDistance = PICK_RADIUS;

  for (let index = 0; index < samples.length; index++) {
    const dx = cursor.x - screenX[index];
    if (dx > bestDistance || dx < -bestDistance) continue;
    const dy = cursor.y - screenY[index];
    if (dy > bestDistance || dy < -bestDistance) continue;
    const distance = Math.hypot(dx, dy);
    if (distance > bestDistance) continue;
    bestDistance = distance;
    best = index;
  }

  if (best === -1) return null;
  return samples[best];
};

export type SampleMarkerArgs = {
  context: CanvasRenderingContext2D;
  geometry: SampleScreenGeometry;
  hovered: CollapsedSample | null;
  inkColor: string;
  surfaceColor: string;
  opacity: number;
};

/**
 * Marks where components actually sit.
 *
 * The most honest control on the chart. Everything between these points is interpolated, and on a
 * real estate that is most of the surface - a viewpoint scored on two 1-5 fields puts a few hundred
 * components on a couple of dozen positions. Without the markers a confidently shaded landscape
 * gives no indication of which parts of it were measured and which were inferred, and the two look
 * exactly alike.
 *
 * Drawn without depth testing, like the wireframe: a marker hidden behind a peak would be a sample
 * the viewer cannot account for, which defeats the purpose.
 */
export const renderSampleMarkers = ({
  context,
  geometry,
  hovered,
  inkColor,
  surfaceColor,
  opacity,
}: SampleMarkerArgs): void => {
  const {
    anchors: { samples, hasSurface, hasContext },
    screenX: screenXs,
    screenY: screenYs,
    surfaceY: surfaceYs,
  } = geometry;
  const hoveredIndex = hovered
    ? (geometry.anchors.indexBySample.get(hovered) ?? -1)
    : -1;

  context.save();
  context.lineWidth = 1.5;
  context.strokeStyle = surfaceColor;
  context.fillStyle = inkColor;

  /* A sample carrying a component the host named. The same `isContext` the point charts read, so
     one host prop reaches all four charts rather than two - and on a surface it answers the
     question a reader arriving from a component actually has, which nothing else on the chart
     does. Emphasis rather than suppression: the state lasts as long as the view. */
  const ringContext = (
    index: number,
    screenX: number,
    screenY: number,
    markerRadius: number
  ): void => {
    if (hasContext[index] !== 1) return;

    context.globalAlpha = opacity;
    context.strokeStyle = inkColor;
    context.lineWidth = CONTEXT_RING_WIDTH;
    context.beginPath();
    context.arc(
      screenX,
      screenY,
      markerRadius + CONTEXT_RING_GAP,
      0,
      Math.PI * 2
    );
    context.stroke();
  };

  for (let index = 0; index < samples.length; index++) {
    if (index === hoveredIndex) continue;
    const screenX = screenXs[index];
    const screenY = screenYs[index];
    const surfaceY = hasSurface[index] === 1 ? surfaceYs[index] : null;

    /* A tether to the fitted surface directly below. The gap is not an error: the markers are the
       components and the surface is a gridded approximation of them, so a component that departs
       from the fit stands off it - which on clustered data is most of them, by up to a sixth of the
       range. Without the tether that reads as markers floating free of the chart; with it, the
       length is the residual and says something true. */
    if (surfaceY !== null && Math.abs(surfaceY - screenY) > 1.5) {
      context.globalAlpha = 0.3 * opacity;
      context.strokeStyle = inkColor;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(screenX, screenY);
      context.lineTo(screenX, surfaceY);
      context.stroke();
    }

    context.globalAlpha = 0.75 * opacity;
    context.strokeStyle = surfaceColor;
    context.lineWidth = 1.5;
    context.beginPath();
    context.arc(screenX, screenY, MARKER_RADIUS, 0, Math.PI * 2);
    context.stroke();
    /* Hollow where there is no surface underneath: that component sits outside the interpolated
       area entirely, and a filled marker would imply the chart has something to say about it. */
    if (surfaceY === null) {
      context.strokeStyle = inkColor;
      context.lineWidth = 1.25;
      context.stroke();
      ringContext(index, screenX, screenY, MARKER_RADIUS);
      continue;
    }
    context.fill();
    ringContext(index, screenX, screenY, MARKER_RADIUS);
  }

  if (hoveredIndex !== -1) {
    const { screenX, screenY, baseY, surfaceY } = toScreenPosition(
      geometry,
      hoveredIndex
    );

    if (surfaceY !== null) {
      context.globalAlpha = 0.55 * opacity;
      context.strokeStyle = inkColor;
      context.lineWidth = 1.5;
      context.beginPath();
      context.moveTo(screenX, screenY);
      context.lineTo(screenX, surfaceY);
      context.stroke();
    }

    /* A plumb line to the plane, which is the only way to say where in the plane a floating marker
       actually is - the thing an unlit 3D chart is worst at. */
    context.globalAlpha = 0.35 * opacity;
    context.strokeStyle = inkColor;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(screenX, screenY);
    context.lineTo(screenX, baseY);
    context.stroke();

    context.globalAlpha = opacity;
    context.beginPath();
    context.arc(screenX, screenY, HOVERED_RADIUS, 0, Math.PI * 2);
    context.fillStyle = inkColor;
    context.fill();
    context.strokeStyle = surfaceColor;
    context.lineWidth = 2;
    context.stroke();

    /* The hovered sample keeps its context ring. The loop above `continue`s past it so the
       enlarged marker can be drawn last, and that skipped the ring with it - so pointing at the
       component the reader arrived from was the one way to make it stop being marked. */
    ringContext(hoveredIndex, screenX, screenY, HOVERED_RADIUS);
  }

  context.restore();
};

export type SampleTooltipArgs = {
  context: CanvasRenderingContext2D;
  sample: CollapsedSample;
  /** Where the sample's marker is on screen. */
  at: { screenX: number; screenY: number };
  viewport: Size;
  xTitle: string;
  yTitle: string;
  zTitle: string;
  format: (value: number) => string;
  inkColor: string;
  surfaceColor: string;
  gridColor: string;
};

/**
 * Names what is under the cursor.
 *
 * Leads with the count, because a position is very often several components rather than one - which
 * is the fact the surface most thoroughly hides. A peak that turns out to be forty stacked
 * applications means something quite different from a peak that is one.
 *
 * Drawn on the canvas rather than as a DOM overlay so the controller stays framework-free and this
 * behaves the same wherever the chart is mounted.
 */
export const renderSampleTooltip = ({
  context,
  sample,
  at,
  viewport,
  xTitle,
  yTitle,
  zTitle,
  format,
  inkColor,
  surfaceColor,
  gridColor,
}: SampleTooltipArgs): void => {
  const lines = [
    `${xTitle || 'x'}: ${format(sample.x)}`,
    `${yTitle || 'y'}: ${format(sample.y)}`,
    `${zTitle || 'z'}: ${format(sample.z)}`,
    ...toComponentNameLines(sample.points),
  ];

  drawTooltip({
    context,
    anchor: at,
    heading: toComponentHeading(sample.points),
    lines,
    viewport,
    inkColor,
    surfaceColor,
    gridColor,
  });
};
